import { formatINR } from "@/lib/api";
import { amountInWords } from "@/lib/amountInWords";
import { devWarn } from "@/lib/logger";
import { templateCss, resolveLogoUrl } from "@/lib/invoiceTemplates";

// ──────────────────────────────────────────────────────────────────────────
// Tax-invoice printer — Rule 46 (CGST Rules 2017) compliant.
//
// All voucher / company / party fields are HTML-escaped before injection to
// prevent XSS from narration / address / item-name text. The rendered HTML
// is loaded via a controlled Blob URL (NEVER document.write) so the print
// window's origin stays sandboxed.
//
// Signature:
//   printInvoice(voucher, { company, partyLedger, copy })
//
//   • voucher       — full voucher doc as returned by /vouchers
//   • company       — { name, address, gstin, state, state_code, phone, email,
//                       pan, logo_url, signatory_name, signatory_designation,
//                       default_terms }
//   • partyLedger   — buyer's ledger row (address, gstin, shipping_address,
//                     state, state_code, contact, email) — may be null
//   • copy          — "Original" | "Duplicate" | "Triplicate" (Rule 48
//                     watermark for goods; defaults to "Original for Recipient")
// ──────────────────────────────────────────────────────────────────────────

const escapeHtml = (s) => {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

/** Multi-line address with <br> between non-empty lines. */
const fmtAddress = (s) =>
  (s || "")
    .split(/\r?\n/)
    .map((l) => escapeHtml(l.trim()))
    .filter(Boolean)
    .join("<br>");

const fmtMoney = (n) => formatINR(n || 0);

/** Stack tax components for one line. Returns {taxable, cgst, sgst, igst, total}.
 * Splits CGST/SGST evenly when items have gst_rate but voucher uses single combined
 * intra-state. For inter-state, the existing voucher.igst totals are used pro-rata.
 */
const calcLineTaxes = (item, voucher) => {
  const taxable = Number(item.amount || 0);
  const gstRate = Number(item.gst_rate || 0);
  if (gstRate === 0 || !voucher) return { taxable, cgst: 0, sgst: 0, igst: 0, total: taxable };
  const totalTax = taxable * (gstRate / 100);
  // Inter-state if voucher posted IGST; else split equally as CGST + SGST.
  if (Number(voucher.igst || 0) > 0 && !voucher.cgst) {
    return { taxable, cgst: 0, sgst: 0, igst: totalTax, total: taxable + totalTax };
  }
  return {
    taxable,
    cgst: totalTax / 2,
    sgst: totalTax / 2,
    igst: 0,
    total: taxable + totalTax,
  };
};

/** HSN summary table aggregating all items by HSN code. Required for GSTR-1
 * cross-verification; also helps the buyer reconcile ITC. */
const buildHsnSummary = (items, voucher) => {
  const byHsn = new Map();
  for (const it of items || []) {
    const key = (it.hsn || "—").trim() || "—";
    const tx = calcLineTaxes(it, voucher);
    const row = byHsn.get(key) || {
      hsn: key, qty: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0,
    };
    row.qty += Number(it.qty || 0);
    row.taxable += tx.taxable;
    row.cgst += tx.cgst;
    row.sgst += tx.sgst;
    row.igst += tx.igst;
    row.total += tx.total;
    byHsn.set(key, row);
  }
  return [...byHsn.values()];
};

export function printInvoice(voucher, { company = {}, partyLedger = null, copy } = {}) {
  if (!voucher) {
    devWarn("printInvoice: no voucher passed");
    return;
  }
  const v = voucher;
  const c = company || {};
  const p = partyLedger || {};

  // ── Heading: Tax Invoice vs Bill of Supply vs Credit/Debit Note ──────
  const typeLabel = (() => {
    switch (v.voucher_type) {
      case "Credit Note": return "Credit Note";
      case "Debit Note": return "Debit Note";
      case "Purchase": return "Purchase Invoice";
      default: return "Tax Invoice";
    }
  })();

  // ── Rule 48 — Original/Duplicate/Triplicate watermark on goods invoices.
  const copyLabel = copy || "Original for Recipient";

  // ── Address blocks — supplier (always), bill-to (from party), ship-to
  //     (only render if explicitly set AND different from bill-to).
  const billTo = (v.bill_to_address || p.address || "").trim();
  const shipTo = (v.ship_to_address || p.shipping_address || "").trim();
  const showShipBlock = shipTo && shipTo !== billTo;

  // ── State code for "Place of Supply" line — Rule 46(g) requires this for
  //     inter-state supplies.
  const posState = v.place_of_supply || p.state || c.state || "";
  const posCode =
    v.place_of_supply_state_code ||
    p.state_code ||
    (p.gstin || "").slice(0, 2) ||
    c.state_code ||
    (c.gstin || "").slice(0, 2) ||
    "";
  const placeOfSupply = posState && posCode ? `${posState} (${posCode})` : (posState || posCode || "—");

  // ── Per-line tax breakdown.
  const items = Array.isArray(v.items) ? v.items : [];
  const anyDiscount = items.some((i) => Number(i.discount_pct || 0) > 0 || Number(i.discount_amount || 0) > 0);
  const lineRows = items
    .map((i, idx) => {
      const t = calcLineTaxes(i, v);
      const gstPct = Number(i.gst_rate || 0);
      const discPct = Number(i.discount_pct || 0);
      const discAmt = Number(i.discount_amount || 0);
      const discCell = anyDiscount
        ? `<td class="num">${discPct ? discPct.toFixed(2) + "%" : "—"}${discAmt ? `<div class="disc-amt">−${fmtMoney(discAmt)}</div>` : ""}</td>`
        : "";
      return `
        <tr>
          <td class="num">${idx + 1}</td>
          <td>${escapeHtml(i.item_name)}</td>
          <td class="hsn">${escapeHtml(i.hsn || "—")}</td>
          <td class="num">${escapeHtml(String(i.qty))} ${escapeHtml(i.unit || "")}</td>
          <td class="num">${fmtMoney(i.rate)}</td>
          ${discCell}
          <td class="num">${fmtMoney(t.taxable)}</td>
          <td class="num">${gstPct ? gstPct.toFixed(1) + "%" : "—"}</td>
          <td class="num">${t.cgst ? fmtMoney(t.cgst) : "—"}</td>
          <td class="num">${t.sgst ? fmtMoney(t.sgst) : "—"}</td>
          <td class="num">${t.igst ? fmtMoney(t.igst) : "—"}</td>
          <td class="num strong">${fmtMoney(t.total)}</td>
        </tr>`;
    })
    .join("");

  // ── Total discount line for the grand totals block.
  const totalDiscount = items.reduce((s, i) => s + Number(i.discount_amount || 0), 0);

  const hsnRows = buildHsnSummary(items, v)
    .map(
      (r) => `
        <tr>
          <td>${escapeHtml(r.hsn)}</td>
          <td class="num">${r.qty.toFixed(2)}</td>
          <td class="num">${fmtMoney(r.taxable)}</td>
          <td class="num">${r.cgst ? fmtMoney(r.cgst) : "—"}</td>
          <td class="num">${r.sgst ? fmtMoney(r.sgst) : "—"}</td>
          <td class="num">${r.igst ? fmtMoney(r.igst) : "—"}</td>
          <td class="num strong">${fmtMoney(r.total)}</td>
        </tr>`
    )
    .join("");

  // ── Totals.
  const subTotal = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const cgst = Number(v.cgst || 0);
  const sgst = Number(v.sgst || 0);
  const igst = Number(v.igst || 0);
  const grand = Number(v.total || subTotal + cgst + sgst + igst);
  const grandWords = amountInWords(grand);

  // ── Original-invoice reference (for credit / debit notes).
  const originalRef =
    (v.voucher_type === "Credit Note" || v.voucher_type === "Debit Note") && v.original_invoice_no
      ? `<div class="ref">Against Invoice: <strong>${escapeHtml(v.original_invoice_no)}</strong>${v.original_invoice_date ? " dated " + escapeHtml(v.original_invoice_date) : ""}</div>`
      : "";

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeHtml(v.voucher_no || "Invoice")}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif; color: #0f172a; padding: 24px 32px; max-width: 900px; margin: 0 auto; font-size: 11.5px; line-height: 1.45; }
  .copy-label { position: fixed; top: 12px; right: 24px; font-size: 10px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.12em; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px double #0f172a; padding-bottom: 14px; }
  .header .supplier h1 { font-size: 20px; margin: 0 0 2px; letter-spacing: -0.01em; }
  .header .supplier .gstin { font-family: 'IBM Plex Mono', monospace; font-size: 11px; color: #1d4ed8; font-weight: 600; }
  .header .supplier .addr { font-size: 11px; color: #475569; max-width: 360px; }
  .header .meta { text-align: right; font-size: 11px; }
  .header .meta .title { font-size: 17px; font-weight: 700; color: #1d4ed8; margin-bottom: 4px; }
  .header .meta .num { font-family: 'IBM Plex Mono', monospace; }
  .logo { max-height: 56px; max-width: 140px; object-fit: contain; margin-bottom: 6px; }
  .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 14px; }
  .party-box { border: 1px solid #cbd5e1; border-radius: 6px; padding: 10px 12px; background: #f8fafc; }
  .party-box .label { font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; margin-bottom: 4px; font-weight: 700; }
  .party-box .name { font-size: 12.5px; font-weight: 600; }
  .party-box .gstin { font-family: 'IBM Plex Mono', monospace; color: #1d4ed8; font-size: 10.5px; margin-top: 2px; }
  .party-box .addr { color: #475569; margin-top: 4px; }
  .pos-bar { display: flex; gap: 18px; flex-wrap: wrap; margin-top: 10px; font-size: 11px; padding: 8px 12px; background: #fff7ed; border: 1px solid #fed7aa; border-radius: 4px; }
  .pos-bar .lbl { color: #64748b; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
  .pos-bar .val { font-weight: 600; }
  .pos-bar .reverse { color: #b91c1c; font-weight: 700; }
  table.lines { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 10.5px; }
  table.lines th { background: #1e293b; color: #fff; padding: 6px 8px; text-align: left; font-weight: 600; text-transform: uppercase; font-size: 9.5px; letter-spacing: 0.05em; border: 1px solid #1e293b; }
  table.lines td { border: 1px solid #cbd5e1; padding: 6px 8px; vertical-align: top; }
  table.lines td.num, table.lines th.num { text-align: right; font-family: 'IBM Plex Mono', monospace; }
  table.lines td.hsn { font-family: 'IBM Plex Mono', monospace; font-size: 10px; }
  table.lines td.strong { font-weight: 700; }
  table.lines td .disc-amt { display: block; font-size: 9px; color: #059669; margin-top: 1px; font-weight: 600; }
  .totals-grid { margin-top: 6px; display: grid; grid-template-columns: 1.4fr 1fr; gap: 12px; }
  .words { padding: 10px 12px; background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 11px; }
  .words .lbl { font-size: 9.5px; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }
  .totals { font-family: 'IBM Plex Mono', monospace; font-size: 11px; border: 1px solid #cbd5e1; border-radius: 4px; padding: 8px 12px; }
  .totals .row { display: flex; justify-content: space-between; padding: 3px 0; }
  .totals .grand { border-top: 2px solid #0f172a; margin-top: 6px; padding-top: 6px; font-weight: 700; font-size: 14px; color: #0f172a; }
  table.hsn-summary { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 10px; }
  table.hsn-summary th, table.hsn-summary td { border: 1px solid #cbd5e1; padding: 5px 8px; text-align: left; }
  table.hsn-summary th { background: #f1f5f9; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; color: #475569; }
  table.hsn-summary td.num { text-align: right; font-family: 'IBM Plex Mono', monospace; }
  table.hsn-summary td.strong { font-weight: 700; }
  .footer { display: grid; grid-template-columns: 1.4fr 1fr; gap: 12px; margin-top: 18px; }
  .bank { font-size: 10.5px; border: 1px solid #cbd5e1; border-radius: 4px; padding: 8px 12px; background: #f8fafc; }
  .bank .lbl { font-size: 9.5px; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; margin-bottom: 4px; }
  .bank .row { display: flex; gap: 6px; }
  .bank .row .k { color: #475569; min-width: 80px; }
  .bank .row .v { font-family: 'IBM Plex Mono', monospace; }
  .sign { font-size: 10.5px; text-align: center; padding: 8px 12px; border: 1px solid #cbd5e1; border-radius: 4px; }
  .sign .for { font-weight: 600; margin-bottom: 36px; }
  .sign .name { font-weight: 600; }
  .sign .desg { font-size: 10px; color: #475569; }
  .ref { margin-top: 8px; padding: 6px 10px; background: #fef3c7; border: 1px solid #fcd34d; border-radius: 4px; font-size: 11px; }
  .terms { margin-top: 14px; font-size: 9.5px; color: #475569; padding: 8px 12px; background: #f8fafc; border-radius: 4px; white-space: pre-line; }
  .meta-row { display: flex; gap: 20px; flex-wrap: wrap; margin-top: 8px; font-size: 10.5px; }
  .meta-row .k { color: #64748b; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.04em; }
  .meta-row .v { font-family: 'IBM Plex Mono', monospace; font-weight: 600; }
  @media print {
    body { padding: 16px 20px; }
    .copy-label { position: absolute; }
    @page { size: A4 portrait; margin: 12mm; }
  }
  /* Ship 3 — template override (modern | classic | compact). Overrides come
     last so they win the specificity war without needing !important on
     shared rules. */
  ${templateCss(c.invoice_template)}
</style></head><body>

<div class="copy-label">${escapeHtml(copyLabel)}</div>

<div class="header">
  <div class="supplier">
    ${c.logo_url ? `<img src="${escapeHtml(resolveLogoUrl(c.logo_url))}" class="logo" alt="logo"/>` : ""}
    <h1>${escapeHtml(c.name || "—")}</h1>
    ${c.address ? `<div class="addr">${fmtAddress(c.address)}</div>` : ""}
    ${c.phone || c.email ? `<div class="addr" style="margin-top:3px">${[c.phone, c.email].filter(Boolean).map(escapeHtml).join(" • ")}</div>` : ""}
    ${c.gstin ? `<div class="gstin" style="margin-top:4px">GSTIN ${escapeHtml(c.gstin)}${c.pan ? ` · PAN ${escapeHtml(c.pan)}` : ""}</div>` : ""}
  </div>
  <div class="meta">
    <div class="title">${escapeHtml(typeLabel)}</div>
    <div><span style="color:#64748b">No: </span><span class="num"><strong>${escapeHtml(v.voucher_no || "—")}</strong></span></div>
    <div><span style="color:#64748b">Date: </span><span class="num">${escapeHtml(v.date || "")}</span></div>
    ${v.reference ? `<div><span style="color:#64748b">Ref: </span><span class="num">${escapeHtml(v.reference)}</span></div>` : ""}
  </div>
</div>

${originalRef}

<div class="parties">
  <div class="party-box">
    <div class="label">Bill To</div>
    <div class="name">${escapeHtml(v.party_ledger_name || p.name || "—")}</div>
    ${billTo ? `<div class="addr">${fmtAddress(billTo)}</div>` : ""}
    ${p.gstin ? `<div class="gstin">GSTIN ${escapeHtml(p.gstin)}</div>` : `<div class="gstin" style="color:#94a3b8">Unregistered</div>`}
    ${p.contact_person || p.contact || p.email ? `<div class="addr" style="margin-top:3px;font-size:10.5px">${[p.contact_person, p.contact, p.email].filter(Boolean).map(escapeHtml).join(" • ")}</div>` : ""}
  </div>
  ${
    showShipBlock
      ? `<div class="party-box">
           <div class="label">Ship To</div>
           <div class="name">${escapeHtml(v.party_ledger_name || p.name || "—")}</div>
           <div class="addr">${fmtAddress(shipTo)}</div>
         </div>`
      : `<div class="party-box" style="background:#fafafa">
           <div class="label">Ship To</div>
           <div class="addr" style="color:#94a3b8;font-style:italic">Same as billing address</div>
         </div>`
  }
</div>

<div class="pos-bar">
  <div><span class="lbl">Place of Supply:</span> <span class="val">${escapeHtml(placeOfSupply)}</span></div>
  <div><span class="lbl">Reverse Charge:</span> <span class="${v.reverse_charge ? "reverse" : ""}">${v.reverse_charge ? "YES" : "No"}</span></div>
  ${v.voucher_type === "Sales" ? `<div><span class="lbl">Document:</span> <span class="val">Tax Invoice (Rule 46)</span></div>` : ""}
</div>

${
  items.length
    ? `<table class="lines">
         <thead><tr>
           <th>#</th><th>Description of goods / services</th><th>HSN/SAC</th>
           <th class="num">Qty</th><th class="num">Rate</th>
           ${anyDiscount ? `<th class="num">Disc.</th>` : ""}
           <th class="num">Taxable value</th>
           <th class="num">GST %</th><th class="num">CGST</th><th class="num">SGST</th>
           <th class="num">IGST</th><th class="num">Amount</th>
         </tr></thead>
         <tbody>${lineRows}</tbody>
       </table>`
    : ""
}

<div class="totals-grid">
  <div class="words">
    <div class="lbl">Amount in words</div>
    <div>${escapeHtml(grandWords)}</div>
  </div>
  <div class="totals">
    ${totalDiscount > 0 ? `<div class="row"><span>Gross value</span><span>${fmtMoney(subTotal + totalDiscount)}</span></div>
    <div class="row" style="color:#059669"><span>Less: Discount</span><span>−${fmtMoney(totalDiscount)}</span></div>` : ""}
    <div class="row"><span>Sub-total (taxable)</span><span>${fmtMoney(subTotal)}</span></div>
    ${cgst ? `<div class="row"><span>Add: CGST</span><span>${fmtMoney(cgst)}</span></div>` : ""}
    ${sgst ? `<div class="row"><span>Add: SGST</span><span>${fmtMoney(sgst)}</span></div>` : ""}
    ${igst ? `<div class="row"><span>Add: IGST</span><span>${fmtMoney(igst)}</span></div>` : ""}
    <div class="row grand"><span>Grand Total</span><span>${fmtMoney(grand)}</span></div>
  </div>
</div>

${
  items.length
    ? `<table class="hsn-summary">
         <thead><tr>
           <th>HSN/SAC</th><th>Total Qty</th><th>Taxable value</th>
           <th>CGST</th><th>SGST</th><th>IGST</th><th>Total</th>
         </tr></thead>
         <tbody>${hsnRows}</tbody>
       </table>`
    : ""
}

<div class="footer">
  <div class="bank">
    <div class="lbl">Pay us at</div>
    ${
      c.default_bank_ledger_id
        ? `<div class="row"><span class="k">Bank A/c</span><span class="v">See attached bank details</span></div>`
        : `<div style="color:#94a3b8;font-style:italic">Bank details not configured. Edit company → "Default Bank Ledger".</div>`
    }
    ${c.email ? `<div class="row"><span class="k">Queries</span><span class="v">${escapeHtml(c.email)}</span></div>` : ""}
  </div>
  <div class="sign">
    <div class="for">For <strong>${escapeHtml(c.name || "—")}</strong></div>
    <div class="name">${escapeHtml(c.signatory_name || "")}</div>
    <div class="desg">${escapeHtml(c.signatory_designation || "Authorised Signatory")}</div>
  </div>
</div>

${v.narration ? `<div class="terms"><strong>Narration: </strong>${escapeHtml(v.narration)}</div>` : ""}
${c.default_terms ? `<div class="terms"><strong>Terms &amp; Conditions:</strong>${"\n" + escapeHtml(c.default_terms)}</div>` : ""}

<div style="margin-top:14px;font-size:9px;color:#94a3b8;text-align:center">
  This is a computer-generated invoice prepared per Rule 46 of the CGST Rules, 2017. Generated by AXIORA.
</div>

</body></html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    URL.revokeObjectURL(url);
    return;
  }
  win.addEventListener("load", () => {
    try { win.print(); } catch (err) { devWarn("Print failed", err); }
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });
}
